/**
 * Preference Graph utilities for smart comparison ranking.
 * Uses transitive closure to minimize comparisons needed.
 */

export interface PreferenceGraph {
  // Direct edges: Map from taskId to Set of taskIds it's more urgent than
  edges: Map<string, Set<string>>;
  // Transitive closure: all reachable pairs (computed from edges)
  closure: Map<string, Set<string>>;
}

export interface TaskWithPreference {
  id: string;
  moreUrgentThan?: string[];
}

/**
 * Build preference graph from tasks' moreUrgentThan arrays
 */
export function buildPreferenceGraph(tasks: TaskWithPreference[]): PreferenceGraph {
  const edges = new Map<string, Set<string>>();
  const closure = new Map<string, Set<string>>();

  // Initialize edges from moreUrgentThan arrays
  for (const task of tasks) {
    const taskEdges = new Set<string>(task.moreUrgentThan || []);
    edges.set(task.id, taskEdges);
    // Initialize closure with direct edges (will be expanded)
    closure.set(task.id, new Set(taskEdges));
  }

  return { edges, closure };
}

/**
 * Compute transitive closure using DFS-based reachability.
 * After this, closure[A] contains all tasks that A is more urgent than (directly or transitively).
 */
export function computeTransitiveClosure(graph: PreferenceGraph): void {
  const { edges, closure } = graph;
  const taskIds = Array.from(edges.keys());

  // For each task, find all reachable tasks via DFS
  for (const taskId of taskIds) {
    const reachable = new Set<string>();
    const stack = Array.from(edges.get(taskId) || []);
    const visited = new Set<string>();

    while (stack.length > 0) {
      const current = stack.pop()!;
      if (visited.has(current)) continue;
      visited.add(current);
      reachable.add(current);

      // Add all tasks that current is more urgent than
      const nextEdges = edges.get(current);
      if (nextEdges) {
        for (const next of nextEdges) {
          if (!visited.has(next)) {
            stack.push(next);
          }
        }
      }
    }

    closure.set(taskId, reachable);
  }
}

/**
 * Check if we know taskA > taskB (directly or transitively)
 * @returns true if A > B, false if B > A, null if unknown
 */
export function isMoreUrgent(
  graph: PreferenceGraph,
  taskA: string,
  taskB: string
): boolean | null {
  const closureA = graph.closure.get(taskA);
  const closureB = graph.closure.get(taskB);

  // Check if A > B (A is more urgent than B)
  if (closureA?.has(taskB)) {
    return true;
  }

  // Check if B > A (B is more urgent than A)
  if (closureB?.has(taskA)) {
    return false;
  }

  // Unknown relationship
  return null;
}

/**
 * Add a comparison result and update closure with transitivity.
 * This is called after each comparison to propagate knowledge.
 */
export function addComparison(
  graph: PreferenceGraph,
  moreUrgentId: string,
  lessUrgentId: string
): void {
  const { edges, closure } = graph;

  // Ensure both tasks have entries
  if (!edges.has(moreUrgentId)) {
    edges.set(moreUrgentId, new Set());
    closure.set(moreUrgentId, new Set());
  }
  if (!edges.has(lessUrgentId)) {
    edges.set(lessUrgentId, new Set());
    closure.set(lessUrgentId, new Set());
  }

  // Add direct edge
  edges.get(moreUrgentId)!.add(lessUrgentId);

  // Update closure: moreUrgentId now reaches lessUrgentId and everything lessUrgentId reaches
  const moreUrgentClosure = closure.get(moreUrgentId)!;
  const lessUrgentClosure = closure.get(lessUrgentId)!;

  moreUrgentClosure.add(lessUrgentId);
  for (const reachable of lessUrgentClosure) {
    moreUrgentClosure.add(reachable);
  }

  // Also update any task that reaches moreUrgentId - they now reach lessUrgentId too
  for (const [, taskClosure] of closure) {
    if (taskClosure.has(moreUrgentId)) {
      taskClosure.add(lessUrgentId);
      for (const reachable of lessUrgentClosure) {
        taskClosure.add(reachable);
      }
    }
  }
}

/**
 * Find the position where a task should be inserted based on known relationships.
 * Returns the rank (1-indexed) if fully determinable, null otherwise.
 *
 * @param graph The preference graph
 * @param taskId The task to find position for
 * @param candidates Sorted list of candidate task IDs (by current rank)
 * @returns { rank: number, isComplete: boolean } - rank if determinable, isComplete if no comparisons needed
 */
export function findKnownPosition(
  graph: PreferenceGraph,
  taskId: string,
  candidates: string[]
): { rank: number; isComplete: boolean } | null {
  if (candidates.length === 0) {
    return { rank: 1, isComplete: true };
  }

  // Count how many candidates we're definitively more urgent than
  // and how many we're definitively less urgent than
  let moreUrgentThanCount = 0;
  let lessUrgentThanCount = 0;
  let unknownCount = 0;

  for (const candidateId of candidates) {
    const relation = isMoreUrgent(graph, taskId, candidateId);
    if (relation === true) {
      moreUrgentThanCount++;
    } else if (relation === false) {
      lessUrgentThanCount++;
    } else {
      unknownCount++;
    }
  }

  // If we know relationship with ALL candidates, we can determine exact position
  if (unknownCount === 0) {
    // Position is right after all tasks we're less urgent than
    // (remember: lower rank = higher priority)
    return { rank: lessUrgentThanCount + 1, isComplete: true };
  }

  // If we have some knowledge, return partial info
  if (moreUrgentThanCount > 0 || lessUrgentThanCount > 0) {
    // We know bounds but need more comparisons
    return null;
  }

  return null;
}

/**
 * Filter candidates to only those with unknown relationships.
 * This allows binary search to skip known comparisons.
 */
export function filterToUnknownCandidates(
  graph: PreferenceGraph,
  taskId: string,
  candidates: string[]
): { unknowns: string[]; knownLessThan: string[]; knownMoreThan: string[] } {
  const unknowns: string[] = [];
  const knownLessThan: string[] = []; // Task is less urgent than these
  const knownMoreThan: string[] = []; // Task is more urgent than these

  for (const candidateId of candidates) {
    const relation = isMoreUrgent(graph, taskId, candidateId);
    if (relation === true) {
      knownMoreThan.push(candidateId);
    } else if (relation === false) {
      knownLessThan.push(candidateId);
    } else {
      unknowns.push(candidateId);
    }
  }

  return { unknowns, knownLessThan, knownMoreThan };
}

/**
 * Topological sort of tasks based on preference graph.
 * Tasks with no incoming edges (most urgent) come first.
 */
export function topologicalSort(
  graph: PreferenceGraph,
  taskIds: string[]
): string[] {
  const taskSet = new Set(taskIds);
  const inDegree = new Map<string, number>();
  const adjList = new Map<string, string[]>();

  // Initialize
  for (const id of taskIds) {
    inDegree.set(id, 0);
    adjList.set(id, []);
  }

  // Build adjacency and in-degree (only for tasks in our set)
  for (const id of taskIds) {
    const moreUrgentThan = graph.edges.get(id) || new Set();
    for (const lessUrgentId of moreUrgentThan) {
      if (taskSet.has(lessUrgentId)) {
        adjList.get(id)!.push(lessUrgentId);
        inDegree.set(lessUrgentId, (inDegree.get(lessUrgentId) || 0) + 1);
      }
    }
  }

  // Kahn's algorithm
  const queue: string[] = [];
  for (const [id, degree] of inDegree) {
    if (degree === 0) {
      queue.push(id);
    }
  }

  const result: string[] = [];
  while (queue.length > 0) {
    const current = queue.shift()!;
    result.push(current);

    for (const neighbor of adjList.get(current) || []) {
      const newDegree = (inDegree.get(neighbor) || 1) - 1;
      inDegree.set(neighbor, newDegree);
      if (newDegree === 0) {
        queue.push(neighbor);
      }
    }
  }

  // If not all tasks are in result, there's a cycle - fall back to original order
  if (result.length !== taskIds.length) {
    console.warn('Cycle detected in preference graph, using original order');
    return taskIds;
  }

  return result;
}

/**
 * Estimate number of comparisons needed to fully order tasks.
 * This is a rough estimate based on unknown pairs.
 */
export function estimateComparisonsNeeded(
  graph: PreferenceGraph,
  taskIds: string[]
): number {
  let unknownPairs = 0;
  const n = taskIds.length;

  // Count pairs with unknown relationships
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (isMoreUrgent(graph, taskIds[i], taskIds[j]) === null) {
        unknownPairs++;
      }
    }
  }

  // Minimum comparisons for merge-sort style: roughly n*log(n) for unknown pairs
  // But with partial knowledge, it's closer to the number of unknown pairs / log(n)
  // This is a rough estimate
  if (unknownPairs === 0) return 0;

  const logN = Math.max(1, Math.log2(n));
  return Math.ceil(unknownPairs / logN);
}

/**
 * Count known vs unknown pairs for progress estimation
 */
export function countKnownPairs(
  graph: PreferenceGraph,
  taskIds: string[]
): { known: number; unknown: number; total: number } {
  let known = 0;
  let unknown = 0;
  const n = taskIds.length;
  const total = (n * (n - 1)) / 2;

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (isMoreUrgent(graph, taskIds[i], taskIds[j]) !== null) {
        known++;
      } else {
        unknown++;
      }
    }
  }

  return { known, unknown, total };
}
